import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LandingLayout from './LandingLayout';
import { Check, ArrowRight, Zap } from 'lucide-react';
import api from '../../api';

export default function Pricing() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [packagesError, setPackagesError] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'annual'
  const navigate = useNavigate();

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setPackagesError('');
    setLoading(true);
    try {
      const response = await api.get('/public/packages');
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
      setPackages([]);
      setPackagesError(
        error.response?.data?.error ||
          error.message ||
          'We could not load pricing plans. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = (packageId) => {
    const q = new URLSearchParams({
      package: packageId,
      cycle: billingCycle,
    });
    navigate(`/signup?${q.toString()}`, {
      state: { packageId, billingCycle },
    });
  };

  if (loading) {
    return (
      <LandingLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading pricing...</p>
          </div>
        </div>
      </LandingLayout>
    );
  }

  return (
    <LandingLayout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
            Start with a 14-day free trial—no card needed to create your account. Add a payment method under Settings → Subscription before the trial ends if you want uninterrupted access after that.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 mb-8">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-white dark:bg-gray-700 text-orange-600 dark:text-orange-500 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                billingCycle === 'annual'
                  ? 'bg-white dark:bg-gray-700 text-orange-600 dark:text-orange-500 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Annual
              <span className="ml-2 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-500 px-2 py-0.5 rounded">
                Save 17%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {packagesError && (
            <div className="max-w-xl mx-auto mb-12 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-6 py-8 text-center">
              <p className="text-red-800 dark:text-red-200 mb-4">{packagesError}</p>
              <button
                type="button"
                onClick={fetchPackages}
                className="px-6 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold"
              >
                Retry
              </button>
              <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                Need help?{' '}
                <a href="mailto:support@sparxmate.co.uk" className="text-orange-600 dark:text-orange-400 font-medium">
                  Contact support
                </a>
              </p>
            </div>
          )}
          {!packagesError && packages.length === 0 && !loading && (
            <p className="text-center text-gray-600 dark:text-gray-400 max-w-xl mx-auto">
              No subscription plans are available at the moment. Please try again later or contact support.
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {!packagesError &&
              packages.map((pkg) => {
              const price = parseFloat(billingCycle === 'monthly' ? pkg.price_monthly : pkg.price_annual) || 0;
              const monthlyEquivalent = billingCycle === 'annual' ? ((parseFloat(pkg.price_annual) || 0) / 12).toFixed(2) : null;
              const features = Array.isArray(pkg.features) ? pkg.features : [];

              return (
                <div
                  key={pkg.id}
                  className={`relative rounded-2xl border-2 p-8 ${
                    pkg.is_featured
                      ? 'border-orange-500 dark:border-orange-500 bg-orange-50 dark:bg-orange-900/10'
                      : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800'
                  }`}
                >
                  {pkg.is_featured && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-orange-600 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center">
                        <Zap className="h-4 w-4 mr-1" />
                        Popular
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {pkg.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      {pkg.description}
                    </p>
                    <div className="mb-2">
                      <span className="text-4xl font-bold text-gray-900 dark:text-white">£</span>
                      <span className="text-5xl font-bold text-gray-900 dark:text-white">
                        {price.toFixed(2)}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        /{billingCycle === 'monthly' ? 'month' : 'year'}
                      </span>
                    </div>
                    {monthlyEquivalent && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        £{monthlyEquivalent} per month billed annually
                      </p>
                    )}
                    {pkg.has_trial && pkg.trial_days > 0 && (
                      <p className="text-sm text-orange-600 dark:text-orange-500 font-medium mt-2">
                        {pkg.trial_days}-day free trial
                      </p>
                    )}
                  </div>

                  <ul className="space-y-4 mb-8">
                    {features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="h-5 w-5 text-orange-600 dark:text-orange-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleGetStarted(pkg.id)}
                    className={`w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center ${
                      pkg.is_featured
                        ? 'bg-orange-600 hover:bg-orange-700 text-white'
                        : 'bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 text-white'
                    }`}
                  >
                    Get Started
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Can I change my plan later?
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Yes, you can upgrade or downgrade your plan at any time. Changes will be prorated.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                What happens after my trial ends?
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                After your 14-day free trial, paid billing applies only once you have added a payment method in Settings → Subscription (you can do this anytime before the trial ends). You can cancel before the trial ends with no charges.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Do you offer refunds?
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                We offer a 30-day money-back guarantee. If you're not satisfied, contact us for a full refund.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Is my data secure?
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Absolutely. We use industry-standard encryption and security practices to protect your data. Your information is stored securely and backed up regularly.
              </p>
            </div>
          </div>
        </div>
      </section>
    </LandingLayout>
  );
}


